import { Blocks } from "@/components/cms/Blocks";
import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import { linkSlot, visibleItems } from "@/components/cms/SectionRenderer";
import { EnquiryForm } from "@/components/forms/EnquiryForm";
import { Accordion } from "@/components/ui/Accordion";
import { ArrowRight, ButtonLink } from "@/components/ui/Button";
import { CTAGroup } from "@/components/ui/CTAGroup";
import { JsonLd } from "@/components/ui/JsonLd";
import { MediaFigure } from "@/components/ui/MediaFigure";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { useContent, useMediaByKey } from "@/content-context";
import { finalCtaFallback } from "@/data/cta-options";
import { charityTrek } from "@/data/media";
import { faqJsonLd } from "@/lib/jsonld";
import { buildMetadata } from "@/lib/seo";
import { pageLoader } from "@/page-data";

export const loader = pageLoader("get-involved");

export const meta = () =>
  buildMetadata({
    title: "Get involved",
    description:
      "Four ways to work with HISCUS Nepal: partner or fund a programme, volunteer with live project work, join a Charity Trek, or propose local collaboration.",
    path: "/get-involved",
  });

function PathwaysSection({ section }: SectionProps) {
  return (
    <Section>
      <RevealGroup as="ul" className="grid gap-6 md:grid-cols-2">
        {visibleItems(section).map((pathway) => (
          <Reveal as="li" key={(pathway.href ?? "") + (pathway.title ?? "")}>
            <div className="group relative flex h-full flex-col rounded-[16px] border border-line bg-white p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-line-strong md:p-8">
              {/*
                Sentence case, not an all-caps kicker. These are full
                sentences naming who a route is for, and letter-spaced capitals
                turned each one into two lines of shouting above the heading.
              */}
              {pathway.eyebrow ? <p className="text-[0.875rem] text-muted">{pathway.eyebrow}</p> : null}
              <h2 className="mt-3" style={{ fontSize: "var(--step-h3)" }}>
                {pathway.title}
              </h2>
              {pathway.text ? <p className="mt-3 text-muted">{pathway.text}</p> : null}
              <a
                href={pathway.href || "/contact"}
                className="mt-auto inline-flex min-h-11 items-center gap-2 pt-6 text-[0.9375rem] font-semibold text-navy"
              >
                <span className="after:absolute after:inset-0 after:content-['']">{pathway.label || "Get in touch"}</span>
                <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

function SupportSection({ section }: SectionProps) {
  const byKey = useMediaByKey();
  const button = linkSlot(section, 0);
  const image = byKey(section.mediaKey);
  const units = visibleItems(section);

  return (
    <Section tone="ivory" id="support" ariaLabelledBy="support-heading">
      <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <SectionHeading id="support-heading" eyebrow={section.eyebrow} title={section.title || "Funding a programme"} />
          <Blocks blocks={section.body} className="mt-6" />

          {/*
            No donation block until there is a verified payment route.
            Enquiries open a conversation instead.
          */}
          {button ? (
            <div className="mt-8">
              <ButtonLink href={button.href}>
                {button.label}
                <ArrowRight />
              </ButtonLink>
            </div>
          ) : null}
        </Reveal>

        <Reveal>
          {/*
            charity: water and Nepal Youth Foundation both make giving concrete
            by attaching an outcome to an amount. HISCUS has no published costs,
            so inventing "$30 funds a session" would be a fabrication. Instead
            these are units of work HISCUS has actually delivered, with the real
            figures attached: the same clarity, without a made-up price list.
          */}
          {units.length ? (
            <div className="surface rounded-[20px] p-7 md:p-9">
              {section.texts?.panelHeading ? (
                <h3 className="text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-blue">
                  {section.texts.panelHeading}
                </h3>
              ) : null}
              {section.texts?.panelNote ? <p className="mt-4 text-[0.9375rem] text-muted">{section.texts.panelNote}</p> : null}

              <dl className="mt-8 space-y-5">
                {units.map((entry) => (
                  <div key={entry.title} className="border-b border-line pb-5 last:border-0 last:pb-0">
                    <dt className="font-serif text-[1.0625rem] text-midnight">{entry.title}</dt>
                    <dd className="mt-1.5 text-[0.9375rem] leading-relaxed text-muted">{entry.text}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}

          {image ? (
            <div className="mt-6">
              <MediaFigure item={image} ratio="16 / 10" sizes="(min-width: 1024px) 45vw, 100vw" showCaption={false} radius={20} />
            </div>
          ) : null}
        </Reveal>
      </div>
    </Section>
  );
}

function EnquirySection({ section }: SectionProps) {
  const byKey = useMediaByKey();
  const image = section.mediaKey ? byKey(section.mediaKey) : charityTrek[1];
  return (
    <Section ariaLabelledBy="enquiry-heading">
      <div className="grid items-start gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:gap-16">
        <Reveal>
          <SectionHeading
            id="enquiry-heading"
            eyebrow={section.eyebrow}
            title={section.title || "Tell us which route interests you"}
            lead={section.lead}
          />
          {image ? (
            <div className="mt-8">
              <MediaFigure item={image} ratio="4 / 3" sizes="(min-width: 1024px) 40vw, 100vw" />
            </div>
          ) : null}
        </Reveal>

        <Reveal>
          <EnquiryForm heading={section.texts?.formHeading || "Send an enquiry"} defaultInterest="general" />
        </Reveal>
      </div>
    </Section>
  );
}

/** The questions, and their structured data only while they are on the page to be read. */
function FaqSection({ section }: SectionProps) {
  const { faqs } = useContent();
  if (!faqs.length) return null;
  return (
    <Section tone="ivory" ariaLabelledBy="faq-heading">
      <SectionHeading
        id="faq-heading"
        eyebrow={section.eyebrow}
        title={section.title || "What people ask before getting in touch"}
      />
      <div className="mt-14">
        <Accordion items={faqs} />
      </div>
      <JsonLd data={faqJsonLd(faqs)} />
    </Section>
  );
}

function SummarySection({ section }: SectionProps) {
  const cards = visibleItems(section);
  const options = cards.length
    ? cards.map((card) => ({
        title: card.title ?? "",
        description: card.text ?? "",
        href: card.href || "/contact",
        action: card.label || "Get in touch",
      }))
    : section.items?.length
      ? []
      : finalCtaFallback;

  return (
    <Section tone="midnight" ariaLabelledBy="paths-heading">
      <SectionHeading
        id="paths-heading"
        eyebrow={section.eyebrow}
        title={section.title || "Pick the route that fits"}
        tone="light"
      />
      {options.length ? (
        <div className="mt-14">
          <CTAGroup options={options} />
        </div>
      ) : null}
    </Section>
  );
}

export const getInvolvedSections: SectionRenderers = {
  "get-involved.pathways": PathwaysSection,
  "get-involved.support": SupportSection,
  "get-involved.enquiry": EnquirySection,
  "get-involved.faq": FaqSection,
  "get-involved.summary": SummarySection,
};

export function GetInvolvedPage() {
  return (
    <>
      <PageHeader
        eyebrow="Get involved"
        title="Four ways to take part"
        lead="Whichever route you take, it starts as a conversation with a person rather than a form that disappears."
        trail={[
          { name: "Home", href: "/" },
          { name: "Get Involved", href: "/get-involved" },
        ]}
      />
      <PageSections renderers={getInvolvedSections} />
    </>
  );
}
